import AccountIcon from "@/assets/images/icons/account-icon.svg";
import { ConfirmSignOutDialog } from "@/components/account/ConfirmSignOutDialog";
import { PERSONAL_HUB } from "@/components/courses/personalHubTokens";
import { Popover } from "@/components/ui/Popover";
import { BodyText, CaptionText, CardTitle } from "@/components/ui/Typography";
import { useAuth } from "@/lib/AuthContext";
import { BRAND_COLORS as C } from "@/lib/brandColors";
import { URLS } from "@/lib/constants";
import { resolveUserDisplayName } from "@/lib/userDisplayName";
import { Ionicons } from "@expo/vector-icons";
import { useRouter } from "expo-router";
import React, { useState } from "react";
import { Alert, Platform, Pressable, View, type ViewStyle } from "react-native";
import { XStack, YStack } from "tamagui";

const HEADER_HOVER_ORANGE = "rgba(242, 102, 58, 1)";
const HAIRLINE = "rgba(0,0,0,0.06)";
/** Same corner radius as announcements / live / bookmarks header popovers (`$4`). */
const POPOVER_RADIUS = 12;
/** Match ExternalLink / BookmarkManager — let the popover finish closing before push. */
const NAV_DEFER_MS = 150;

const PANEL_SHELL_SHADOW =
  Platform.OS === "web"
    ? ({
        boxShadow:
          "0 4px 8px 0 rgba(0, 0, 0, 0.10), 0 0 4px 0 rgba(0, 0, 0, 0.08)",
      } as const)
    : {
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.1,
        shadowRadius: 8,
        elevation: 6,
      };

const WEB_NO_OUTLINE = (
  Platform.OS === "web" ? { outline: "none", outlineStyle: "none" } : undefined
) as ViewStyle | undefined;

function MenuRow({
  label,
  icon,
  onPress,
  destructive,
}: {
  label: string;
  icon: keyof typeof Ionicons.glyphMap;
  onPress: () => void;
  destructive?: boolean;
}) {
  const [hovered, setHovered] = useState(false);
  const tint = destructive ? C.error : C.text;

  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      // Avoid RN-web `<button>` + UA focus ring; keep it a plain pressable.
      style={WEB_NO_OUTLINE}
    >
      <XStack
        ai="center"
        gap={10}
        px={14}
        py={11}
        bg={hovered ? PERSONAL_HUB.tileBg : "transparent"}
        {...(Platform.OS === "web"
          ? ({
              cursor: "pointer",
              transition: "background-color 140ms ease",
            } as object)
          : {})}
      >
        <Ionicons name={icon} size={18} color={tint} />
        <BodyText
          color={tint}
          fontWeight={destructive ? "600" : "400"}
          ta="left"
          f={1}
        >
          {label}
        </BodyText>
      </XStack>
    </Pressable>
  );
}

/**
 * Header account dropdown (tablet / desktop).
 */
export function AccountMenu({ iconSize = 26 }: { iconSize?: number }) {
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [hovered, setHovered] = useState(false);
  const router = useRouter();
  const { user, signOut } = useAuth();

  const isWeb = Platform.OS === "web";
  const email = user?.email?.trim() || null;
  const rawName = resolveUserDisplayName(user?.user_metadata);
  // Don't treat the email as a "name" — that doubles it in the header.
  const name =
    rawName &&
    !(email && rawName.toLowerCase() === email.toLowerCase()) &&
    !rawName.includes("@")
      ? rawName
      : null;

  const go = (href: string) => {
    setOpen(false);
    setTimeout(() => {
      router.push(href as never);
    }, NAV_DEFER_MS);
  };

  // Web only: navigate to the in-app login screen ourselves, otherwise
  // `useWordPressLoginRedirect` bounces to WordPress and signs the user back in
  // from the still-valid WP cookie. On native the (main) guard already redirects
  // once the session clears, so navigating here would render login twice.
  const runSignOut = async () => {
    setSigningOut(true);
    const { error } = await signOut();
    setSigningOut(false);
    setConfirmOpen(false);
    if (error) {
      if (isWeb) {
        if (typeof window !== "undefined") {
          window.alert(error.message || "Failed to sign out");
        }
      } else {
        Alert.alert("Error", error.message || "Failed to sign out");
      }
      return;
    }
    if (isWeb) router.replace("/auth/login" as never);
  };

  const content = (
    <YStack
      w="100%"
      bg={C.cardBg}
      br={POPOVER_RADIUS}
      overflow="hidden"
      style={PANEL_SHELL_SHADOW}
    >
      <YStack px={14} py={12} gap={2} borderBottomWidth={1} borderColor={HAIRLINE}>
        {name ? (
          <CardTitle color={C.text} numberOfLines={1} ta="left">
            {name}
          </CardTitle>
        ) : null}
        {email ? (
          <CaptionText color={C.muted} numberOfLines={1} ta="left">
            {email}
          </CaptionText>
        ) : null}
      </YStack>

      <YStack py={4}>
        <MenuRow
          label="Account"
          icon="settings-outline"
          onPress={() => go("/settings")}
        />
        {/* Web has no in-app membership screen: go straight to the
            WordPress account page (billing, invoices, plan changes). */}
        <MenuRow
          label="Membership"
          icon="card-outline"
          onPress={() => {
            if (isWeb) {
              setOpen(false);
              if (typeof window !== "undefined") {
                window.location.href = URLS.MY_ACCOUNT;
              }
              return;
            }
            go("/membership");
          }}
        />
      </YStack>

      <YStack h={1} bg={HAIRLINE} />

      <YStack py={4}>
        <MenuRow
          label="Sign out"
          icon="log-out-outline"
          destructive
          onPress={() => {
            setOpen(false);
            setTimeout(() => setConfirmOpen(true), NAV_DEFER_MS);
          }}
        />
      </YStack>
    </YStack>
  );

  return (
    <>
      <Popover
        open={open}
        onOpenChange={(next) => {
          if (next === open) return;
          setOpen(next);
        }}
        content={content}
        placement="bottom-end"
        offset={30}
        width={260}
        maxWidth={260}
        maxHeight={420}
        contentBackgroundColor="transparent"
        bareContent
        contentBorderRadius={POPOVER_RADIUS}
      >
        <View style={WEB_NO_OUTLINE}>
          <XStack
            ai="center"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={isWeb ? { cursor: "pointer", ...WEB_NO_OUTLINE } : undefined}
          >
            <AccountIcon
              width={iconSize}
              height={iconSize}
              color={isWeb && hovered ? HEADER_HOVER_ORANGE : "white"}
            />
          </XStack>
        </View>
      </Popover>

      <ConfirmSignOutDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        onConfirm={() => void runSignOut()}
        busy={signingOut}
      />
    </>
  );
}
